import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Globe, Compass, Heart, Users, Mountain, Sparkles, UsersRound, FileCheck, Plane, Star } from "lucide-react";
import SEO from "@/components/SEO";
import SearchWidget from "@/components/SearchWidget";
import Marquee from "@/components/Marquee";
import Reveal from "@/components/Reveal";
import { DestinationCard, PackageCard, VisaCard, GroupTourCard, OfferCard } from "@/components/Cards";
import FaqAccordion from "@/components/FaqAccordion";
import { useContent } from "@/lib/content";
import { CATEGORIES, WHY_US, HOME_FAQS, TESTIMONIALS, IMG, TAGLINE } from "@/lib/site";
import { useEnquiry } from "@/components/EnquiryModal";

const ICONS = { Globe, Compass, Heart, Users, Mountain, Sparkles, UsersRound, FileCheck, Plane };

const heroLine = {
  hidden: { y: "110%" },
  show: (i) => ({ y: "0%", transition: { duration: 0.9, delay: 0.15 + i * 0.14, ease: [0.22, 1, 0.36, 1] } }),
};

export default function Home() {
  const { packages, destinations, visas, groupTours, testimonials, offers } = useContent();
  const testimonialList = (testimonials && testimonials.length) ? testimonials : TESTIMONIALS;
  const { openEnquiry } = useEnquiry();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0.2]);

  const featured = packages.slice(0, 6);
  const intl = destinations.filter((d) => d.region === "International").slice(0, 4);
  const india = destinations.filter((d) => d.region === "India").slice(0, 4);
  const featuredVisas = visas.slice(0, 4);
  const featuredTours = groupTours.slice(0, 2);

  return (
    <div data-testid="home-page">
      <SEO description="D Travel Link — Thane's trusted travel agency for international holidays, India tours, honeymoons, group departures, visa assistance and flights & hotels. Linking Dreams to Destinations." />

      {/* HERO */}
      <section ref={heroRef} className="relative min-h-[92vh] flex items-center overflow-hidden grain">
        <motion.div style={{ y: bgY }} className="absolute inset-0 -bottom-24">
          <img src={IMG.hero} alt="Tropical beach destination — D Travel Link holidays" className="w-full h-full object-cover" />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-b from-navydeep/70 via-navy/35 to-navydeep/80" />
        <motion.div style={{ opacity: fade }} className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-28">
          <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
                    className="text-xs font-bold tracking-[0.3em] uppercase text-sky-300" data-testid="hero-overline">
            {TAGLINE}
          </motion.p>
          <h1 data-testid="hero-headline" className="mt-4 font-heading font-extrabold tracking-tight text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.08]">
            {["YOUR JOURNEY.", "OUR EXPERTISE."].map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span custom={i} variants={heroLine} initial="hidden" animate="show" className="block">
                  {i === 1 ? <span className="text-sky-300">{line}</span> : line}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.55 }}
                    data-testid="hero-subheadline"
                    className="mt-5 max-w-xl text-base sm:text-lg text-slate-200 leading-relaxed">
            Handpicked holidays, seamless planning and unforgettable experiences — crafted personally by our Thane travel experts.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.75 }} className="mt-9 max-w-4xl">
            <SearchWidget />
          </motion.div>
        </motion.div>
      </section>

      <Marquee />

      {/* CATEGORIES */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="text-xs font-bold tracking-[0.25em] uppercase text-skybrand">Find Your Kind of Holiday</p>
            <h2 className="mt-2 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">Travel, Your Way</h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {CATEGORIES.map((c, i) => {
              const Icon = ICONS[c.icon];
              return (
                <Reveal key={c.name} delay={i * 0.06}>
                  <Link to={c.to} data-testid={c.testid}
                        className="group relative block h-56 rounded-2xl overflow-hidden shadow-sm hover:shadow-2xl transition-shadow duration-300">
                    <img loading="lazy" src={c.image} alt={c.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                    <div className="absolute inset-0 bg-gradient-to-t from-navydeep/90 via-navydeep/30 to-transparent" />
                    <div className="absolute bottom-0 p-5 w-full flex items-end justify-between">
                      <div>
                        <p className="text-[11px] font-bold tracking-[0.2em] uppercase text-gold">{c.tag}</p>
                        <h3 className="font-heading text-xl font-bold text-white">{c.name}</h3>
                      </div>
                      <span className="w-10 h-10 rounded-full bg-white/15 backdrop-blur flex items-center justify-center transition-colors group-hover:bg-royal">
                        <Icon className="w-5 h-5 text-white" />
                      </span>
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* FEATURED PACKAGES */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <p className="text-xs font-bold tracking-[0.25em] uppercase text-skybrand">Handpicked For You</p>
              <h2 className="mt-2 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">Featured Holidays</h2>
              <p className="mt-2 text-slate-500 max-w-xl">Starting prices are tentative — every final quote is customised to your dates, hotels and travellers.</p>
            </div>
            <Link to="/international-holidays" data-testid="view-all-packages-link"
                  className="inline-flex items-center gap-2 rounded-full border border-navy px-5 py-2.5 text-sm font-semibold text-navy hover:bg-navy hover:text-white transition-colors">
              View All <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featured.map((p, i) => (
              <Reveal key={p.slug} delay={i * 0.06}><PackageCard pkg={p} /></Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TRAVEL OFFERS */}
      {(offers || []).filter((o) => o.active !== false).length > 0 && (
        <section className="py-16 lg:py-24 bg-gradient-to-b from-amber-50/70 to-white" data-testid="offers-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <p className="text-xs font-bold tracking-[0.25em] uppercase text-red-600">Limited Period · Save Big</p>
              <h2 className="mt-2 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">Travel Offers</h2>
              <p className="mt-2 text-slate-500 max-w-xl">Discounted departures and seasonal steals — grab them before the validity date runs out.</p>
            </Reveal>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {(offers || []).filter((o) => o.active !== false).map((o, i) => (
                <Reveal key={o.id} delay={(i % 3) * 0.06}><OfferCard offer={o} packages={packages} /></Reveal>
              ))}
            </div>
            <p className="mt-6 text-[11px] text-slate-400">*Offer prices are per person on twin sharing, tentative and subject to availability on the travel date. Validity as mentioned on each offer.</p>
          </div>
        </section>
      )}

      {/* DESTINATIONS STRIPS */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <p className="text-xs font-bold tracking-[0.25em] uppercase text-skybrand">Around the World</p>
              <h2 className="mt-2 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">Top International Destinations</h2>
            </div>
            <Link to="/international-holidays" className="inline-flex items-center gap-2 text-sm font-semibold text-royal hover:text-navy transition-colors">
              Explore all <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {intl.map((d, i) => <Reveal key={d.slug} delay={i * 0.05}><DestinationCard dest={d} /></Reveal>)}
          </div>
          <Reveal className="mt-14 flex items-end justify-between flex-wrap gap-4">
            <div>
              <p className="text-xs font-bold tracking-[0.25em] uppercase text-skybrand">Incredible India</p>
              <h2 className="mt-2 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">Explore Your Own Backyard</h2>
            </div>
            <Link to="/india-holidays" className="inline-flex items-center gap-2 text-sm font-semibold text-royal hover:text-navy transition-colors">
              Explore all <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {india.map((d, i) => <Reveal key={d.slug} delay={i * 0.05}><DestinationCard dest={d} /></Reveal>)}
          </div>
        </div>
      </section>

      {/* WHY US — numbered manifesto */}
      <section className="py-16 lg:py-24 bg-navy text-white grain relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="text-xs font-bold tracking-[0.25em] uppercase text-sky-300">Why D Travel Link</p>
            <h2 className="mt-2 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight max-w-xl">
              Eight reasons travellers trust us with their most important days of the year
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 rounded-2xl overflow-hidden">
            {WHY_US.map((w, i) => (
              <Reveal key={w.title} delay={i * 0.05} className="bg-navy">
                <div className="p-6 h-full hover:bg-white/5 transition-colors">
                  <span className="font-display italic text-3xl text-gold">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="mt-3 font-heading font-bold text-white">{w.title}</h3>
                  <p className="mt-2 text-sm text-slate-400 leading-relaxed">{w.description}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* GROUP TOURS + VISA TEASERS */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <Reveal className="flex items-end justify-between gap-4">
                <div>
                  <p className="text-xs font-bold tracking-[0.25em] uppercase text-skybrand">Fixed Departures</p>
                  <h2 className="mt-2 font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Upcoming Group Tours</h2>
                </div>
                <Link to="/group-tours" className="inline-flex items-center gap-1.5 text-sm font-semibold text-royal hover:text-navy shrink-0">All tours <ArrowRight className="w-4 h-4" /></Link>
              </Reveal>
              <div className="mt-8 space-y-6">
                {featuredTours.map((t) => <Reveal key={t.slug}><GroupTourCard tour={t} /></Reveal>)}
              </div>
            </div>
            <div>
              <Reveal className="flex items-end justify-between gap-4">
                <div>
                  <p className="text-xs font-bold tracking-[0.25em] uppercase text-skybrand">Hassle-Free</p>
                  <h2 className="mt-2 font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Visa Assistance</h2>
                </div>
                <Link to="/visa" className="inline-flex items-center gap-1.5 text-sm font-semibold text-royal hover:text-navy shrink-0">All visas <ArrowRight className="w-4 h-4" /></Link>
              </Reveal>
              <div className="mt-8 grid sm:grid-cols-2 gap-6">
                {featuredVisas.map((v, i) => <Reveal key={v.slug} delay={i * 0.05}><VisaCard visa={v} /></Reveal>)}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="text-xs font-bold tracking-[0.25em] uppercase text-skybrand">Traveller Stories</p>
            <h2 className="mt-2 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">What Our Travellers Say</h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonialList.map((t, i) => (
              <Reveal key={t.id || i} delay={i * 0.06}>
                <figure data-testid={`testimonial-card-${i}`} className="h-full bg-white rounded-2xl border border-slate-200/80 p-6 shadow-sm">
                  {t.placeholder ? (
                    <span className="inline-block rounded-full bg-amber-50 border border-amber-200 px-3 py-1 text-[10px] font-bold tracking-wide uppercase text-amber-600">Sample — awaiting real reviews</span>
                  ) : (
                    <span className="inline-block rounded-full bg-emerald-50 border border-emerald-200 px-3 py-1 text-[10px] font-bold tracking-wide uppercase text-emerald-600">Verified Traveller</span>
                  )}
                  <div className="mt-3 flex gap-0.5" aria-label="5 star review">
                    {[...Array(5)].map((_, s) => <Star key={s} className="w-4 h-4 fill-amber-400 text-amber-400" />)}
                  </div>
                  <blockquote className="mt-3 font-display italic text-lg text-slate-700 leading-relaxed">"{t.text}"</blockquote>
                  <figcaption className="mt-4 text-sm">
                    <span className="font-semibold text-slate-900">{t.name}</span>
                    <span className="text-slate-400"> · {t.trip}</span>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center">
            <p className="text-xs font-bold tracking-[0.25em] uppercase text-skybrand">Good To Know</p>
            <h2 className="mt-2 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">Frequently Asked Questions</h2>
          </Reveal>
          <Reveal className="mt-8">
            <FaqAccordion faqs={HOME_FAQS} testidPrefix="home-faq" />
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-20 overflow-hidden grain">
        <img src={IMG.santorini} alt="Plan your next holiday with D Travel Link" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-navydeep/80" />
        <div className="relative max-w-3xl mx-auto px-4 text-center">
          <Reveal>
            <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-white">Ready to start planning?</h2>
            <p className="mt-3 text-slate-300">Tell us your dream destination — we will handle everything else.</p>
            <button onClick={() => openEnquiry({})} data-testid="home-cta-quote-button"
                    className="mt-7 rounded-full bg-gold px-8 py-3.5 font-heading font-bold text-navydeep hover:bg-amber-400 transition-colors">
              Get My Custom Quote
            </button>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
